import React from "react"
import { graphql } from "gatsby"
import { ButtonParser } from "components/button/ButtonParser"
import { Content } from "components/content/Content"
import { Image } from "components/image/Image"
import { PriceTier } from "./PriceTier"
import type { PriceTierElements } from "./price-tier-types"
import type { StrapiImageBuilder } from "_types/AssetsImage"
import type { ButtonElements } from "_types/Button"
import type { LinkedPage } from "_types/LinkedPage"
import "./service.module.scss"

export interface SingleServiceProps {
  anchor_reference: string | null
  associated_page: LinkedPage | null
  button_text: string | null
  custom_anchor_reference: string | null
  ending_footage?: number
  flag?: string | null
  footage_type?: "linear" | "square"
  icon?: StrapiImageBuilder
  id: string
  images?: StrapiImageBuilder
  index: number
  initial_fee: string
  migrated_associated_page?: string | null
  name?: string
  pests_covered?: {
    data: {
      pests_covered: string
    }
  }
  price: string
  price_term?: string
  service_features?: {
    data: {
      service_features: string
    }
  }
  starting_footage?: number
}

export const SingleService = ({
  anchor_reference,
  associated_page,
  button_text,
  custom_anchor_reference,
  ending_footage,
  footage_type,
  id,
  images,
  index,
  initial_fee,
  migrated_associated_page,
  name,
  pests_covered,
  price_term,
  price,
  service_features,
  starting_footage,
}: SingleServiceProps) => {
  const anchorReference = custom_anchor_reference || anchor_reference
  const hasAnchorReference = !!anchorReference

  const button: ButtonElements = {
    associated_page,
    button_text,
    anchor_reference,
    custom_anchor_reference,
    migrated_associated_page,
    button_style: hasAnchorReference ? "cta button" : "navigation button",
    custom_call_tracking_number: null,
    external_link: null,
    id: `${id}-${index}`,
    use_main_office_call_tracking_number: false,
  }

  const altBg = index % 2 ? false : true

  const priceTiers: PriceTierElements = {
    ending_footage,
    footage_type,
    initial_fee,
    price,
    starting_footage,
  }

  const pestsCovered =
    typeof pests_covered?.data === "object"
      ? pests_covered.data?.pests_covered
      : pests_covered?.data
  const serviceFeatures =
    typeof service_features?.data === "object"
      ? service_features.data?.service_features
      : service_features?.data

  return (
    <section
      className={
        altBg ? "single-service single-service--alt-bg" : "single-service"
      }
    >
      <div className="fr-container fr-container--large u-clearfix">
        {name && <h2 className="single-service__title">{name}</h2>}
        <div className="single-service__pricing">
          {images && (
            <Image
              alt={images[0].alternativeText || ""}
              className="single-service__image"
              image={images[0]}
            />
          )}
          {priceTiers && (
            <PriceTier
              priceTerm={price_term || "monthly"}
              priceTier={priceTiers}
            />
          )}
        </div>
        <div className="single-service__content">
          {serviceFeatures && <Content content={serviceFeatures} />}
          {pestsCovered && <Content content={pestsCovered} />}
        </div>
        {button_text && (
          <div className="single-service__button">
            <ButtonParser buttons={[button]} />
          </div>
        )}
      </div>
    </section>
  )
}

export const strapiQuery = graphql`
  fragment StrapiServiceFragment on STRAPI__COMPONENT_CONTENT_BLOCK_SERVICE {
    anchor_reference
    button_text
    custom_anchor_reference
    flag
    id
    initial_fee
    migrated_associated_page
    name
    price
    price_term
    pricing_disclaimer
    starting_footage
    strapi_component
    associated_page {
      slug
    }
    icon {
      ...StrapiImageFragment
    }
    images {
      ...StrapiImageFragment
    }
    pests_covered {
      data {
        pests_covered
      }
    }
    service_features {
      data {
        service_features
      }
    }
  }
`
